import { useEffect, useMemo, useState } from 'react'
import { PartnerRepository } from '../data/partnerRepository'
import type { PartnerConnectionView, PartnerInviteResult } from '../domain/partner'

function invitationDraft(link: string): string {
  return `Hey love,

I am doing some real work on myself through Primal Masculine Man because I want to become a better man, partner and father. Part of that work is learning to understand the people I love through their own voice, not only through what I assume.

I would value your honest perspective. This is completely optional. Your answers live in your own private space and are not shown to me unless you choose to share a specific answer with me. What you keep private can still help my Coach shape more thoughtful guidance for me.

There is no right answer and no pressure to protect my feelings. Silence or no is allowed.

If you would like to take part, this private invitation is for you:
${link}`
}

function whatsappDigits(value: string): string {
  return value.replace(/[^0-9]/g, '')
}

function inviteFailureMessage(error: unknown): string {
  const code = String((error as { code?: unknown })?.code ?? '')
  const detail = String((error as { message?: unknown })?.message ?? '')
  if (detail.includes('active partner connection or invitation')) return 'A private invitation is already open. Refresh this page to see its current state.'
  if (detail.includes('Give this invitation space')) return 'This address is inside the respectful reinvite pause. The previous invitation must be given space before another can be created.'
  if (detail.includes('safety-sensitive route')) return 'Partner invitations are paused while a safety-sensitive route is active.'
  if (detail.includes('cannot invite your own account')) return 'Use your partner\'s own email address, not the email signed into this account.'
  if (code.includes('permission-denied')) return 'This signed-in account is not currently enabled for the private partner test.'
  if (code.includes('unauthenticated')) return 'Your session has ended. Sign in again, then create the invitation.'
  if (code.includes('invalid-argument')) return 'Check her email address and the invitation acknowledgement, then try again.'
  return 'The secure invitation could not be created. Nothing was sent or stored. Please try once more.'
}

export function PartnerConnectionCard() {
  const repository = useMemo(() => new PartnerRepository(), [])
  const [view, setView] = useState<PartnerConnectionView | null>(null)
  const [invite, setInvite] = useState<PartnerInviteResult | null>(null)
  const [email, setEmail] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [draft, setDraft] = useState('')
  const [noticeAccepted, setNoticeAccepted] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => { void repository.getConnection().then(setView).catch(() => undefined) }, [repository])
  if (!view || (!view.enrollmentEnabled && view.connection.state === 'none')) return null

  async function createInvite() {
    setMessage('')
    try {
      const next = await repository.createInvite(email, Boolean(whatsapp.trim()), noticeAccepted, crypto.randomUUID())
      const link = new URL(next.sharePath, window.location.origin).toString()
      setInvite(next)
      setDraft(invitationDraft(link))
      setView(await repository.getConnection())
    } catch (error) {
      setMessage(inviteFailureMessage(error))
    }
  }

  function openEmailDraft() {
    window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent('A private invitation from me')}&body=${encodeURIComponent(draft)}`
  }

  function openWhatsAppDraft() {
    const digits = whatsappDigits(whatsapp)
    if (digits.length < 8) {
      setMessage('Add her full WhatsApp number with country code before opening the draft.')
      return
    }
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(draft)}`, '_blank', 'noopener,noreferrer')
  }

  if (view.connection.state === 'active') return <section className="partner-connection-card">
    <p className="eyebrow">Her private room / connected</p>
    <h2>Her voice remains hers.</h2>
    <p>Her private answers can inform Coach-reviewed Personalisation. Only words she deliberately marked for direct sharing can appear below.</p>
    <div className="partner-shared-summary"><strong>{view.connection.shared.responses?.length ?? 0}</strong><span>answers she chose to share directly</span></div>
    {view.connection.shared.responses?.map((item) => <article className="partner-shared-answer" key={item.responseId}><small>{item.question}</small><p>{item.answer}</p></article>)}
    {view.connection.shared.currentSupport && <p><strong>Current support:</strong> {view.connection.shared.currentSupport.capacity}. This signal disappears after 24 hours.</p>}
  </section>

  return <section className="partner-connection-card partner-invitation-studio">
    <p className="eyebrow">Invite her voice / without pressure</p>
    <h2>Give her a private room of her own.</h2>
    <p>She receives a secure browser space with her own consent, correction, export, unlink and deletion controls. Ignoring or declining creates no profile and requires no explanation.</p>
    {view.connection.state === 'invited' && !invite ? <p className="partner-message">A private invitation is already open. To protect her from pressure, create only one invitation and give it space.</p> : !invite && <>
      <div className="partner-form-grid">
        <label><span>Her email</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label><span>Her WhatsApp number <small>(optional)</small></span><input type="tel" autoComplete="tel" placeholder="+61." value={whatsapp} onChange={(event) => setWhatsapp(event.target.value)} /><small>Used only in this browser to open your draft. The raw number is not stored by PMM.</small></label>
      </div>
      <label className="partner-check"><input type="checkbox" checked={noticeAccepted} onChange={(event) => setNoticeAccepted(event.target.checked)} /><span>I will send this once, make participation genuinely optional, and accept silence or no without consequence.</span></label>
      <button type="button" disabled={!email || !noticeAccepted} onClick={() => void createInvite()}>Create her secure invitation</button>
    </>}
    {invite && <div className="partner-invite-result">
      <strong>Your editable invitation message</strong>
      <p>Add your own voice if you wish. Keep the privacy promise and secure link intact.</p>
      <textarea className="partner-invite-draft" value={draft} onChange={(event) => setDraft(event.target.value)} rows={14} />
      <div className="partner-invite-actions">
        <button type="button" onClick={openEmailDraft}>Open email draft</button>
        {whatsapp.trim() && <button type="button" onClick={openWhatsAppDraft}>Open WhatsApp draft</button>}
        <button type="button" className="secondary-action" onClick={() => void navigator.clipboard.writeText(draft).then(() => setMessage('Invitation message copied.'))}>Copy message</button>
      </div>
      <small>The secure link expires {new Date(invite.expiresAt).toLocaleString()}. PMM does not send or store your edited message.</small>
    </div>}
    {message && <p className="partner-message" role="status">{message}</p>}
  </section>
}

